// Generic imports
import { v4 as uuidv4 } from 'uuid';
import { parse } from 'csv-parse/browser/esm/sync';
import Papa from 'papaparse';
import _ from 'lodash';
import { formulaFromLCLWM } from '../../utils/Formula.js';

// Local imports
import airportPorts from '../../airports-parsed.json';

async function convertMonadaCsv({ data, org, supplier, unlocode, seaPorts, supplierEmail }) {
    const csv = parse(data, { columns: true, });
    const successful = [];
    const failed = [];

    let count = 0;
    // Iterate through each records
    for await (const record of csv) {
        let source = null;
        let sourceType = 'everywhere';
        if (record.source) {
            source = seaPorts.find(p => p.code === record.source);
            sourceType = 'port';
            if (!source) {
                source = airportPorts.find(p => p.ident === record.source || p.iata_code === record.source);
                sourceType = 'port';
            }
            if (!source && (record.source.indexOf('(unlocode)') > -1)) {
                source = unlocode.find(p => p.code === record.source.split(' ')[0]);
                sourceType = 'unlocode';
            }
            if (!source) {
                sourceType = 'error';
                console.error(`Could not find`, record.source, unlocode);
            }
        }

        let destination = null;
        let destinationType = 'everywhere';
        if (record.destination) {
            destination = seaPorts.find(p => p.code === record.destination);
            destinationType = 'port';
            if (!destination) {
                destination = airportPorts.find(p => p.ident === record.destination || p.iata_code === record.destination);
                destinationType = 'port';
            }
            if (!destination && (record.destination.indexOf('(unlocode)') > -1)) {
                destination = unlocode.find(p => p.code === record.destination.split(' ')[0]);
                destinationType = 'unlocode';
            }
            if (!destination) {
                destinationType = 'error';
                console.error(`Could not find`, record.destination, unlocode);
            }
        }

        const transportationMethod = (record.transportationMethod || '').toLowerCase();

        if ((!transportationMethod || sourceType === 'error' || destinationType === 'error')) {
            failed.push({ index: count, source: record.source, destination: record.destination, product: record['Type/Size'] });
            count++;
            continue;
        }

        const supplier = record.supplier;
        const supplierEmail = record.supplierEmail;

        if (transportationMethod === 'sea') {
            if (sourceType === 'everywhere') {
                source = null;
            } else if (sourceType === 'port') {
                source = {
                    text: `${source.code} - ${source.portname || source.text}`,
                    countryCode: source.code.substr(0,2),
                    type: source.portname ? 'port' : 'unlocode',
                    id: source.code
                };
            } else {
                source = {
                    text: `${source.code} - ${source.text}`,
                    countryCode: source.code.substr(0,2),
                    type: 'unlocode',
                    id: source.code
                };
            }

            if (destinationType === 'everywhere') {
                destination = null;
            } else if (destinationType === 'port') {
                destination = {
                    text: `${destination.code} - ${destination.portname || destination.text}`,
                    countryCode: destination.code.substr(0,2),
                    type: destination.portname ? 'port' : 'unlocode',
                    id: destination.code
                };
            } else {
                destination = {
                    text: `${destination.code} - ${destination.text}`,
                    countryCode: destination.code.substr(0,2),
                    type: 'unlocode',
                    id: destination.code
                };
            }
        }

        if (transportationMethod === 'air') {
            if (sourceType === 'everywhere') {
                source = null;
            } else if (sourceType === 'port') {
                source = {
                    text: `${source.iata_code} (${source.local_code || source.ident}) - ${source.name}`,
                    countryCode: source.iso_country,
                    type: 'port',
                    id: source.iata_code,
                };
            } else {
                source = {
                    text: `${source.code} - ${source.text}`,
                    countryCode: source.code.substr(0,2),
                    type: 'unlocode',
                    id: source.code
                };
            }

            if (destinationType === 'everywhere') {
                destination = null;
            } else if (destinationType === 'port') {
                destination = {
                    text: `${destination.iata_code} (${destination.local_code || destination.ident}) - ${destination.name}`,
                    countryCode: destination.iso_country,
                    type: 'port',
                    id: destination.iata_code,
                };
            } else {
                destination = {
                    text: `${destination.code} - ${destination.text}`,
                    countryCode: destination.code.substr(0,2),
                    type: 'unlocode',
                    id: destination.code
                };
            }
        }

        const validFrom = record.validFrom;
        const validUntil = record.validUntil;
        // No transit time in the file (or not a number): -1, Monada's "unknown". An empty cell parsed
        // as NaN was sent as null, which the rates API rejects on publish.
        const parsedTransit = parseInt(record.transitTime);
        const transitTime = Number.isFinite(parsedTransit) ? parsedTransit : -1;
        const transshipment = record.transshipment || '';
        const formId = _.findKey(org.forms, f => f.title === record.form && !f.deleted);

        let rates = [];

        if ((record.product || '').toLowerCase() === 'route') {
            rates = successful.filter(s => s.source?.id === source?.id &&
                s.destination?.id === destination?.id &&
                s.offer.transitTime === transitTime &&
                s.product.dangerous === ((record.dangerous || '').toLowerCase() === 'true'));
        } else if (!org.forms[formId]) {
            failed.push({ index: count, source: record.source, destination: record.destination, product: record['Type/Size'] });
            count++;
            continue;
        } else {
            const productId = uuidv4();
            const isLCL = record.product === '1 cbm' || record.product === '1 ton' || record.product.toLowerCase() === 'lcl';
            const isAirFreight = record.product.toLowerCase() === 'air freight';
            const type = isLCL ? 'LCL' : isAirFreight ? 'Air freight' : record.product;
            const densityRatio = isLCL && record.densityRatio ? parseFloat(record.densityRatio) : undefined;
            const rate = {
                type: record.type,
                transportationMethod,
                ...(source ? { source } : {}),
                ...(destination ? { destination } : {}),
                supplier: {
                    organization: supplier,
                    email: supplierEmail,
                },
                product: {
                    id: productId,
                    type,
                    dangerous: (record.dangerous || '').toLowerCase() === 'true',
                    quantity: 1,
                    ...(record.product.includes('Reefer') ? { celsiusOrFahrenheit: 'c', temperature: 0 } : {}),
                    ...(record.product.includes('Flat') ? { length: 0, height: 0, width: 0, cmOrInches: 'cm' } : {}),
                    ...(record.product.includes('Open Top') ? { height: 0, cmOrInches: 'cm' } : {}),
                    ...(isAirFreight ? { kgOrLbs: 'kg', cmOrInches: 'cm', calculations: [{ id: 'default', method: 'chargeable-weight', chargeableWeight: 1, weight: 1, quantity: 1 }] } : {}),
                    ...(isLCL ? { kgOrLbs: 'kg', cmOrInches: 'cm', calculations: [{ id: 'default', method: 'volume', volume: 1, weight: 1000, quantity: 1 }], ...(densityRatio ? { densityRatio } : {}) } : {}),
                },
                offer: {
                    transitTime,
                    validFrom,
                    validUntil,
                    transshipment,
                    template: formId,
                    // Only the form's charges that the file has a column for. A supplier's form can
                    // hold many more charges than one file fills (an agents' form with 12 charges, a
                    // file with 2); copying all of them gave every rate a row of zero charges that
                    // weren't on the file. A section left with no charges is left out.
                    sections: org.forms[formId].sections.map(s => ({ ...s, fields: (s.fields || []).filter(f => f.title in record) })).filter(s => s.fields.length).map(s => ({
                        id: uuidv4(),
                        title: s.title,
                        description: s.description,
                        offers: [{
                            id: uuidv4(),
                            fields: s.fields.map(f => ({
                                id: uuidv4(),
                                template: f.id,
                                title: f.title,
                                type: f.type,
                                values: f.type === 'flat' ? { flat: { value: 0, currency: f.currency || s.defaultCurrency } } :
                                    f.type === 'string' ? { string: { value: '' } } :
                                    f.type === 'per-unit' ? { [productId]: { value: 0, currency: f.currency || s.defaultCurrency } } :
                                    f.type === 'per-unit-type' ? { [productId]: { value: 0, currency: f.currency || s.defaultCurrency } } :
                                    f.type === 'per-unit-weight' ? { [productId]: { value: 0, currency: f.currency || s.defaultCurrency } } :
                                    f.type === 'date' ? { date: { value: '' } } :
                                    {}
                            })),
                        }]
                    }))
                }
            };
            successful.push({ rate, index: count });
            rates = [rate];
        }

        rates.forEach(rate => {
            rate.offer.sections.forEach(section => section.offers[0].fields.forEach(f => {
                const v = record[f.title];
                if (!v) return;

                if (f.type === 'flat') {
                    if (v.startsWith('=')) {
                        f.values.flat.formula = v.split('|')[0].substr(1);
                    } else {
                        f.values.flat.value = parseFloat(v.split('|')[0]) || 0;
                    }
                    f.values.flat.currency = v.split('|')[1];
                }

                // Gross Weight (per-unit-weight) is keyed by product like the chargeable-weight
                // charges, so it takes the same value/formula/currency path.
                if ((f.type === 'per-unit') || (f.type === 'per-unit-type') || (f.type === 'per-unit-weight')) {
                    // LCL W/M formula:
                    //   =wm(perTon,perCbm)
                    //   =wm(perTon,perCbm,densityRatio)
                    //   =wm(perTon,perCbm,densityRatio,minPerTon,minPerCbm)
                    const wmMatch = v.match(/^=wm\(\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*,\s*([\d.]+)\s*)?)?\)(.*)$/i);
                    if (wmMatch) {
                        const perTon = parseFloat(wmMatch[1]) || 0;
                        const perCbm = parseFloat(wmMatch[2]) || 0;
                        const dr = parseFloat(wmMatch[3]) || rate.product?.densityRatio || 1000;
                        const minPerTon = parseFloat(wmMatch[4]) || 0;
                        const minPerCbm = parseFloat(wmMatch[5]) || 0;
                        f.values[rate.product.id].formula = formulaFromLCLWM({ perTon, perCbm, densityRatio: dr, minPerTon, minPerCbm });
                        f.values[rate.product.id].value = 0;
                        const currencyPart = wmMatch[6]?.replace(/^\|/, '').trim();
                        if (currencyPart) f.values[rate.product.id].currency = currencyPart;
                    } else if (v.startsWith('=')) {
                        f.values[rate.product.id].formula = v.split('|')[0].substr(1);
                    } else {
                        f.values[rate.product.id].value = parseFloat(v.split('|')[0]) || 0;
                    }
                    if (!wmMatch) f.values[rate.product.id].currency = v.split('|')[1] || f.values[rate.product.id].currency;
                }

                if (f.type === 'string') {
                    f.values.string.value = v.split('|')[0];
                }

                if (f.type === 'date') {
                    f.values.date.value = parseDateCell(v.split('|')[0]);
                }
            }))

            rate.offer.transshipment = rate.offer.transshipment || record['transshipment'];
        });
        count++;
    }

    return { successful, failed };
}

// Inverse of convertMonadaCsv: serialize an array of Monada rate objects (DB structure)
// back into the Monada import CSV format, so the produced file round-trips through
// convertMonadaCsv. One rate => one row; each charge field becomes its own column,
// formatted as `value|currency`, `=formula|currency`, or a plain string.
function portCell(p) {
    if (!p || !p.id) return '';
    return p.type === 'unlocode' ? `${p.id} (unlocode)` : p.id;
}

// Monada import CSV wants validity as yyyy-mm-dd. Rates store it as an epoch
// (ms or s) representing local midnight, so a naive UTC date can land a day
// early — shift +12h to round to the intended calendar day. Pass through
// already-formatted strings untouched.
function dateCell(v) {
    if (v === undefined || v === null || v === '') return '';
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
    const num = typeof v === 'number' ? v : (/^\d{10,13}$/.test(String(v)) ? Number(v) : NaN);
    if (Number.isNaN(num)) return String(v);
    const ms = num < 1e12 ? num * 1000 : num;            // seconds vs milliseconds
    return new Date(ms + 12 * 3600 * 1000).toISOString().slice(0, 10);
}

// A date charge column holds yyyy-mm-dd; also accept dd/mm/yyyy, the format the upload
// dialog labels its own dates in. Anything else is kept as written rather than guessed at.
function parseDateCell(v) {
    const text = (v || '').trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
    const dmy = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (dmy) return `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`;
    return text;
}

function chargeCell(field, productId) {
    if (!field) return '';
    if (field.type === 'string') {
        return field.values?.string?.value ?? '';
    }
    if (field.type === 'date') {
        return field.values?.date?.value ?? '';
    }
    const cell = field.type === 'flat' ? field.values?.flat : field.values?.[productId];
    if (!cell) return '';
    const currency = cell.currency ? `|${cell.currency}` : '';
    if (cell.formula) return `=${cell.formula}${currency}`;
    const value = cell.value;
    if (value === undefined || value === null || value === '' || value === 0) return '';
    return `${value}${currency}`;
}

function ratesToMonadaCsv({ rates, org }) {
    const FIXED = ['source', 'destination', 'transportationMethod', 'type', 'product', 'dangerous',
        'validFrom', 'validUntil', 'transitTime', 'transshipment', 'form', 'densityRatio', 'supplier', 'supplierEmail'];

    // Collect charge-field column titles in first-seen order for a stable header.
    const chargeTitles = [];
    const seenTitles = new Set();
    (rates || []).forEach(rate => {
        (rate.offer?.sections || []).forEach(section => {
            (section.offers?.[0]?.fields || []).forEach(f => {
                if (f.title && !seenTitles.has(f.title)) {
                    seenTitles.add(f.title);
                    chargeTitles.push(f.title);
                }
            });
        });
    });

    const rows = (rates || []).map(rate => {
        const productId = rate.product?.id;
        const row = {
            source: portCell(rate.source),
            destination: portCell(rate.destination),
            transportationMethod: rate.transportationMethod || '',
            type: rate.type || '',
            product: rate.product?.type || '',
            dangerous: rate.product?.dangerous ? 'true' : 'false',
            validFrom: dateCell(rate.offer?.validFrom),
            validUntil: dateCell(rate.offer?.validUntil),
            transitTime: rate.offer?.transitTime ?? '',
            transshipment: rate.offer?.transshipment || '',
            form: org?.forms?.[rate.offer?.template]?.title || '',
            densityRatio: rate.product?.densityRatio ?? '',
            supplier: rate.supplier?.organization || '',
            supplierEmail: rate.supplier?.email || '',
        };
        chargeTitles.forEach(title => { row[title] = ''; });
        (rate.offer?.sections || []).forEach(section => {
            (section.offers?.[0]?.fields || []).forEach(f => {
                if (!f.title) return;
                const cell = chargeCell(f, productId);
                if (cell !== '') row[f.title] = cell;
            });
        });
        return row;
    });

    return Papa.unparse({ fields: [...FIXED, ...chargeTitles], data: rows });
}

export { convertMonadaCsv, ratesToMonadaCsv };
